import { sortByKeys } from "@notemeal/utils-sort";
import { GenericOrderPreview } from "./Ticket/types";

const ONE_SECOND_MS = 1000;

export type TicketOrderKey = `${string}:${string}`;

export type PickupTimeOverride = {
  effectivePickup: string;
  /** Server pickup at the time the override was written; cleared if server pickup changes. */
  serverPickupAtOverride: string;
};

export type PickupTimeOverrides = ReadonlyMap<TicketOrderKey, PickupTimeOverride>;

export const getTicketOrderKey = (order: Pick<GenericOrderPreview, "id" | "__typename">): TicketOrderKey =>
  `${order.__typename}:${order.id}`;

export const getOrderPickupTime = (order: GenericOrderPreview): string =>
  order.__typename === "MenuOrder" ? order.pickupTime : order.parentBulkOrder.pickupTime;

export const getEffectivePickupTime = (order: GenericOrderPreview, overrides: PickupTimeOverrides): string =>
  overrides.get(getTicketOrderKey(order))?.effectivePickup ?? getOrderPickupTime(order);

/**
 * Neighbor interpolation on the absolute timeline so new tickets (real server
 * pickup on the 15‑minute grid) still intercalate naturally.
 */
export const effectivePickupAfterDrop = ({
  leftEffectiveIso,
  rightEffectiveIso,
}: {
  leftEffectiveIso: string | null;
  rightEffectiveIso: string | null;
}): string => {
  if (leftEffectiveIso == null && rightEffectiveIso == null) {
    return new Date(0).toISOString();
  }
  if (leftEffectiveIso == null) {
    return new Date(new Date(rightEffectiveIso!).getTime() - ONE_SECOND_MS).toISOString();
  }
  if (rightEffectiveIso == null) {
    return new Date(new Date(leftEffectiveIso).getTime() + ONE_SECOND_MS).toISOString();
  }

  const leftMs = new Date(leftEffectiveIso).getTime();
  const rightMs = new Date(rightEffectiveIso).getTime();
  if (rightMs - leftMs <= 1) {
    return new Date(leftMs + 1).toISOString();
  }
  return new Date(Math.floor((leftMs + rightMs) / 2)).toISOString();
};

export const reconcilePickupTimeOverrides = <Order extends GenericOrderPreview>(
  overrides: PickupTimeOverrides,
  orders: readonly Order[]
): Map<TicketOrderKey, PickupTimeOverride> => {
  const next = new Map<TicketOrderKey, PickupTimeOverride>();
  for (const order of orders) {
    const key = getTicketOrderKey(order);
    const override = overrides.get(key);
    if (!override) {
      continue;
    }
    if (override.serverPickupAtOverride !== getOrderPickupTime(order)) {
      continue;
    }
    next.set(key, override);
  }
  return next;
};

const arrayMove = <T>(items: readonly T[], fromIndex: number, toIndex: number): T[] => {
  const next = [...items];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
};

/**
 * After a strip drag (or equivalent index move), write a single override for the
 * moved ticket based on its new neighbors' effective pickup times.
 */
export const overridesAfterIndexMove = <Order extends GenericOrderPreview>(
  sortedOrders: readonly Order[],
  fromIndex: number,
  toIndex: number,
  prevOverrides: PickupTimeOverrides,
  reconcileOrders: readonly Order[] = sortedOrders
): Map<TicketOrderKey, PickupTimeOverride> => {
  const overrides = reconcilePickupTimeOverrides(prevOverrides, reconcileOrders);
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= sortedOrders.length ||
    toIndex >= sortedOrders.length
  ) {
    return overrides;
  }

  const moved = arrayMove(sortedOrders, fromIndex, toIndex);
  const left = toIndex > 0 ? moved[toIndex - 1] : null;
  const right = toIndex < moved.length - 1 ? moved[toIndex + 1] : null;
  const movedOrder = moved[toIndex];

  const effectivePickup = effectivePickupAfterDrop({
    leftEffectiveIso: left ? getEffectivePickupTime(left, overrides) : null,
    rightEffectiveIso: right ? getEffectivePickupTime(right, overrides) : null,
  });

  overrides.set(getTicketOrderKey(movedOrder), {
    effectivePickup,
    serverPickupAtOverride: getOrderPickupTime(movedOrder),
  });
  return overrides;
};

/**
 * Realize an explicit desired order (Reorder modal Save) by ensuring each
 * adjacent pair is correctly ordered under effective pickup times.
 */
export const overridesFromDesiredOrder = <Order extends GenericOrderPreview>(
  desiredOrder: readonly Order[],
  prevOverrides: PickupTimeOverrides,
  reconcileOrders: readonly Order[] = desiredOrder
): Map<TicketOrderKey, PickupTimeOverride> => {
  const overrides = reconcilePickupTimeOverrides(prevOverrides, reconcileOrders);

  for (let i = 0; i < desiredOrder.length; i++) {
    const order = desiredOrder[i];
    const left = i > 0 ? desiredOrder[i - 1] : null;
    const right = i < desiredOrder.length - 1 ? desiredOrder[i + 1] : null;

    const leftEffectiveIso = left ? getEffectivePickupTime(left, overrides) : null;
    const rightEffectiveIso = right ? getEffectivePickupTime(right, overrides) : null;
    const currentEffectiveIso = getEffectivePickupTime(order, overrides);
    const currentMs = new Date(currentEffectiveIso).getTime();
    const leftMs = leftEffectiveIso != null ? new Date(leftEffectiveIso).getTime() : null;
    const rightMs = rightEffectiveIso != null ? new Date(rightEffectiveIso).getTime() : null;

    const fitsLeft =
      leftMs == null ||
      leftMs < currentMs ||
      (leftMs === currentMs && new Date(left!.createdAt).getTime() < new Date(order.createdAt).getTime());
    const fitsRight =
      rightMs == null ||
      currentMs < rightMs ||
      (currentMs === rightMs && new Date(order.createdAt).getTime() < new Date(right!.createdAt).getTime());

    if (fitsLeft && fitsRight) {
      continue;
    }

    overrides.set(getTicketOrderKey(order), {
      effectivePickup: effectivePickupAfterDrop({ leftEffectiveIso, rightEffectiveIso }),
      serverPickupAtOverride: getOrderPickupTime(order),
    });
  }

  return overrides;
};

export const sortMenuOrdersWithOverrides = <Order extends GenericOrderPreview>(
  orders: readonly Order[],
  overrides: PickupTimeOverrides = new Map()
): readonly Order[] => {
  const reconciled = reconcilePickupTimeOverrides(overrides, orders);

  // Use a dedicated sort key — never overwrite MenuOrder.pickupTime, which the
  // ticket UI reads for display. Effective pickup is sort-only / session-local.
  return sortByKeys(
    orders
      .filter(mo => mo.itemsByDiningStation.length > 0)
      .map(o => ({
        ...o,
        createdAtInv: new Date(o.createdAt).getTime(),
        effectivePickupSort: getEffectivePickupTime(o, reconciled),
      })),
    ["effectivePickupSort", "createdAtInv"]
  );
};
